import { COLOR_TOKEN } from 'src/components/color-token';
/* eslint-disable react/display-name */
import {
	Select,
	styled,
	Text,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTrackingManager } from 'src/feature-app';
import { OneFormState } from 'src/types';

type WrapperProps = {
	disabled?: boolean;
	error?: boolean;
	formTheme?: string;
};

const SelectWrapper = styled.div<WrapperProps>`
	opacity: ${props => (props.disabled ? '0.5' : '1')};
	& span::before {
		display: none;
	}
	svg {
		fill: ${props => COLOR_TOKEN[props.formTheme]?.content.primary};
	}
	&:hover svg {
		fill: ${props => COLOR_TOKEN[props.formTheme]?.content.hover};
	}
	option {
		color: ${props => COLOR_TOKEN[props.formTheme]?.content.primary};
		padding-left: 12px;
	}

	& > label {
		box-shadow: ${props =>
			props.error
				? `${
						COLOR_TOKEN[props.formTheme]?.signal.error
				  } 0px 0px 0px 2px`
				: `${
						COLOR_TOKEN[props.formTheme]?.border.default
				  } 0px 0px 0px 1px`};
	}
`;
const SelectOption = styled.option``;

interface SelectProps {
	options: Array<any>;
	name: string;
	label: string;
	value?: any;
	defaultValue?: string;
	isFloating?: boolean;
	required?: boolean;
	disabled?: boolean;
	error?: boolean;
	getDealers?: (provincia: string) => void;
	setFormValue?: (selValueIndex: number) => void;
	onClick?: (e?: any) => void;
	setOption?: React.Dispatch<React.SetStateAction<any>>;
	updateFields?: boolean;
	handleChange: any;
	isRequired: boolean;
	innerRef?: any;
}

export const SelectExtended = React.forwardRef(
	(props: SelectProps, ref: React.Ref<any>) => {
		const [selItem, setSelItem] = useState<string | null>(null);
		const [showOptions, setShowOptions] = useState<boolean>(false);

		const [keyValuePair, setKeyValuePair] = useState();
		const {
			name,
			options,
			setOption,
			updateFields,
			value,
			handleChange,
			isRequired,
			label,
			defaultValue,
			isFloating,
			innerRef,
		} = props;
		const wrapperRef = useRef<HTMLHeadingElement>(null);
		const dispatch = useDispatch();
		const trackingManager = useTrackingManager();
		const { formInfo } = useSelector((state: OneFormState) => state);
		const { errors, setValue, register } = useFormContext();

		const handleClickOutside = (e: any) => {
			if (!wrapperRef.current?.contains(e.target)) {
				setShowOptions(false);
			}
		};

		const setItem = (option: any, index: number) => {
			if (!props.disabled) {
				if (typeof option !== 'object') {
					setSelItem(option);
					if (setOption) setOption(option);
				} else {
					const dealerName = option.props.children[0].props.children;
					setSelItem(dealerName);
				}
				if (props.setFormValue) props.setFormValue(index);
				setShowOptions(false);
			}
		};

		useEffect(() => {
			setSelItem(null);
		}, [props.options]);

		useEffect(() => {
			document.addEventListener('mousedown', handleClickOutside);
			// función que se llama cuando la componente se desmonta
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}, [wrapperRef.current]);

		useEffect(() => {
			updateFields
				? dispatch({
						type: 'UPDATE_FIELDS',
						payload: {
							[name]: keyValuePair ? keyValuePair : value,
						},
				  })
				: undefined;
		}, [keyValuePair, value]);

		useEffect(() => {
			if (value) {
				setTimeout(() => {
					setSelItem(value);
				}, 0);
			}
		}, [value]);

		const isRequiredRef = isRequired
			? register({
					required: {
						value: true,
						message: 'Requerido',
					},
			  })
			: register;

		return (
			<Text
				appearance={TokenTextAppearance.copy200}
				color={TokenTextColor.secondary}
			>
				<SelectWrapper
					onClick={() => {
						trackingManager.trackFormFieldClick(
							{
								contentId: label,
							},
							{
								FormFieldName: name,
							},
						);
						props.onClick();
					}}
					disabled={props.disabled}
					ref={wrapperRef}
					formTheme={formInfo.formTheme || 'main'}
					error={errors[name] && !value ? true : false}
				>
					<Select
						disabled={props.disabled}
						label={label}
						isFloating={isFloating}
						value={value}
						name={name}
						onChange={event => {
							event.preventDefault();
							handleChange(event);
						}}
						required={isRequired}
						autoFocus={false}
					>
						{isFloating && (
							<SelectOption value="" disabled>
								{label}
							</SelectOption>
						)}
						{options.map((option: any, index: number) => {
							if (typeof option === 'string') {
								return (
									<SelectOption
										onClick={e => {
											e.preventDefault();
											setItem(option, index);
										}}
										key={index}
										value={option}
									>
										{option}
									</SelectOption>
								);
							} else {
								return (
									<option
										onClick={e => {
											e.preventDefault();
											setItem(option.name, index);
											setKeyValuePair(option.code);
										}}
										key={index}
										value={option.name}
									>
										{option.name}
									</option>
								);
							}
						})}
					</Select>
				</SelectWrapper>
				<input
					name={name}
					type="hidden"
					ref={innerRef ? innerRef : isRequiredRef}
					required={isRequired}
					value={value}
				/>{' '}
				{/* Input hidden para registrar ref. <Select> no admite ref */}
			</Text>
		);
	},
);
